import { memo } from "react";
import { Box, Typography, Button } from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { grey, blue } from "@mui/material/colors";
import { useHistory } from "react-router-dom";

type EmailStagedPreviewProps = {
  statusSummary?: string;
  to?: string;
  subject?: string;
  /** Optional explicit body; if absent it is derived from `message`. */
  body?: string;
  message?: string;
  approveUrl?: string;
};

// The tool's `message` embeds the body between the "**Body:**" marker and the
// trailing attachments / review-link sections. Extract it so we can render it
// verbatim — an email body is plain text, not markdown, so its own line breaks
// must be preserved (markdown would collapse single newlines into spaces).
const extractBody = (message?: string): string => {
  if (!message) return "";
  const marker = "**Body:**\n";
  const idx = message.indexOf(marker);
  if (idx === -1) return "";
  let body = message.slice(idx + marker.length);
  const attachIdx = body.indexOf("\n**Attachments:**");
  if (attachIdx !== -1) body = body.slice(0, attachIdx);
  const reviewIdx = body.indexOf("Review and approve at:");
  if (reviewIdx !== -1) body = body.slice(0, reviewIdx);
  return body.trim();
};

// Pull the "**Attachments:** a, b" line out of `message`, if present.
const extractAttachments = (message?: string): string => {
  if (!message) return "";
  const match = message.match(/\*\*Attachments:\*\*\s*([^\n]+)/);
  return match ? match[1].trim() : "";
};

const LabelRow = ({ label, value }: { label: string; value?: string }) =>
  value ? (
    <Typography variant="body2" sx={{ fontSize: "0.8rem", mb: 0.25 }}>
      <Box component="span" sx={{ fontWeight: 600, color: grey[800] }}>
        {label}:
      </Box>{" "}
      {value}
    </Typography>
  ) : null;

/**
 * Deterministic preview for the "Send Email" tool's staged-for-approval result.
 *
 * Renders recipient / subject / body + a Review & Approve action directly from
 * the tool result, instead of relying on the LLM to echo them in its reply
 * (which it does inconsistently — see #29553 follow-up). The body is shown
 * verbatim (pre-wrap) since email bodies are plain text, not markdown.
 */
const EmailStagedPreview = memo(
  ({
    statusSummary,
    to,
    subject,
    body,
    message,
    approveUrl,
  }: EmailStagedPreviewProps) => {
    const history = useHistory();
    const bodyText = body || extractBody(message);
    const attachments = extractAttachments(message);

    return (
      <Box
        data-testid="aa-email-staged-preview"
        sx={{
          mt: 0,
          mb: 1,
          borderRadius: "0 0 6px 6px",
          border: `1px solid ${grey[300]}`,
          borderTop: "none",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.25,
            py: 0.6,
            borderTop: `1px solid ${grey[300]}`,
            bgcolor: blue[50],
          }}
        >
          <EmailOutlinedIcon sx={{ fontSize: 16, color: blue[700] }} />
          <Typography
            variant="caption"
            sx={{ color: grey[800], fontSize: "0.75rem", fontWeight: 600 }}
          >
            {statusSummary || "Email staged for approval"}
          </Typography>
        </Box>

        <Box sx={{ px: 1.5, py: 1, bgcolor: grey[50] }}>
          <LabelRow label="To" value={to} />
          <LabelRow label="Subject" value={subject} />
          <LabelRow label="Attachments" value={attachments || undefined} />

          {bodyText && (
            <Box sx={{ mt: 0.75 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: grey[700],
                  display: "block",
                  mb: 0.25,
                }}
              >
                Body
              </Typography>
              <Typography
                data-testid="aa-email-staged-preview-body"
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  fontSize: "0.8rem",
                  color: grey[800],
                  wordBreak: "break-word",
                }}
              >
                {bodyText}
              </Typography>
            </Box>
          )}
        </Box>

        {approveUrl && (
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: grey[50],
              borderTop: `1px solid ${grey[200]}`,
            }}
          >
            <Button
              onClick={() => history.push(approveUrl)}
              variant="contained"
              size="small"
              endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              data-testid="aa-email-staged-approve-button"
            >
              Review &amp; Approve
            </Button>
          </Box>
        )}
      </Box>
    );
  },
);

EmailStagedPreview.displayName = "EmailStagedPreview";

export default EmailStagedPreview;
